/* eslint-disable */
import React, { useState, useRef, useEffect } from 'react';
import { useSendChatMessage } from './mutation/useSendChatMessage';
import {
  ChatWidget,
  ChatWindow,
  Header,
  SupportText,
  MessagesContainer,
  MessageRow,
  MessageBubble,
  InputArea,
  InputField,
  SendButton,
  TypingIndicator,
  CloseButton,
  FileName,
  IconCircle,
} from './styles';

export {}; // Prevents TypeScript from treating this file as global

type ChatbotProps = {
  userId: string;
  chatId: string;
};

type MessageType = { from: 'bot' | 'user'; text: string; fileName?: string };

const SendArrowIcon: React.FC<{ disabled?: boolean }> = ({ disabled }) => (
  <svg width="50" height="50" viewBox="0 0 24 24" fill={disabled ? '#b3b3b3' : '#6146D7'} style={{ display: 'block' }}>
    <path d="M3 21l18-9L3 3v7l15 2-15 2z" fill={disabled ? '#b3b3b3' : '#6146D7'} />
  </svg>
);

const MessageIconCircle = () => (
  <IconCircle>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <rect x="5" y="8" width="14" height="8" rx="4" fill="#fff" />
      <path d="M5 16L4 20L9 17" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="7.5" y="11" width="8" height="1.4" rx="0.7" fill="#6146D7" />
      <rect x="7.5" y="13.5" width="6" height="1.4" rx="0.7" fill="#6146D7" />
    </svg>
  </IconCircle>
);

const Chatbot: React.FC<ChatbotProps> = ({ userId, chatId }) => {
  // ✅ Renamed state variables to avoid naming conflicts
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<MessageType[]>([{ from: 'bot', text: 'Hi, how can I help you?' }]);
  const [chatInput, setChatInput] = useState('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [currentChatId, setCurrentChatId] = useState(chatId);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { mutate: sendMessage, isLoading } = useSendChatMessage();

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setChatInput(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 140);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    console.log('Current chat ID here:', currentChatId);
  }, [currentChatId]);

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file) setAttachedFile(file);
    if (e.target.value) e.target.value = '';
  };

  const handleSend = () => {
    if ((!chatInput.trim() && !attachedFile) || isLoading) return;

    const userMsg: MessageType = {
      from: 'user',
      text: chatInput || (attachedFile ? `[File] ${attachedFile.name}` : ''),
      fileName: attachedFile?.name,
    };

    setChatMessages(msgs => [...msgs, userMsg]);
    setChatInput('');

    sendMessage(
      {
        userId,
        chatId: currentChatId,
        from: 'user',
        content: userMsg.text,
        file: attachedFile || undefined,
      },
      {
        onSuccess: (data: { reply: string; chatId?: string }) => {
          setChatMessages(msgs => [...msgs, { from: 'bot', text: data.reply }]);
          if (data.chatId && data.chatId !== currentChatId) setCurrentChatId(data.chatId);
        },
        onError: (error: { message: string }) => {
          setChatMessages(msgs => [...msgs, { from: 'bot', text: error.message }]);
        },
        onSettled: () => setAttachedFile(null),
      }
    );
  };

  useEffect(() => {
    if (isChatOpen && messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isChatOpen]);

  return (
    <>
      {!isChatOpen && (
        <ChatWidget onClick={() => setIsChatOpen(true)}>
          <MessageIconCircle /> Ask Support
        </ChatWidget>
      )}
      {isChatOpen && (
        <ChatWindow>
          <Header>
            ESS Chat Support
            <CloseButton onClick={() => setIsChatOpen(false)}>&times;</CloseButton>
          </Header>
          <SupportText>ESS Support</SupportText>

          <MessagesContainer>
            {chatMessages.map((msg, idx) => (
              <MessageRow key={idx} from={msg.from}>
                <MessageBubble from={msg.from}>
                  {msg.text}
                  {msg.fileName && <div style={{ fontSize: 11, color: '#6146D7' }}>📎 {msg.fileName}</div>}
                </MessageBubble>
              </MessageRow>
            ))}
            {isLoading && <TypingIndicator>Bot is typing...</TypingIndicator>}
            <div ref={messagesEndRef} />
          </MessagesContainer>

          <InputArea>
            <InputField
              ref={textareaRef}
              rows={1}
              value={chatInput}
              onChange={handleInputChange}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={isLoading}
              placeholder="Send your message..."
            />
            <SendButton disabled={isLoading} onClick={handleSend}>
              <SendArrowIcon disabled={isLoading} />
            </SendButton>
          </InputArea>

          {attachedFile && (
            <FileName>
              📎 {attachedFile.name}
              <button onClick={() => setAttachedFile(null)}>×</button>
            </FileName>
          )}
        </ChatWindow>
      )}
    </>
  );
};

export default Chatbot;
